import { isDarkSquare, isPromotionSquare } from './path.js';
import type { RuleSet } from './rules/types.js';
import { opponentOf, type Color, type Move, type Piece, type Square } from './types.js';

export type BoardCell = Piece | null;

function indexOf(square: Square, boardSize: number): number {
  return square.row * boardSize + square.col;
}

/** Compact one-char encoding for hashing positions (used by repetition rules). */
const PIECE_GLYPH: Record<Color, Record<'man' | 'king', string>> = {
  white: { man: 'w', king: 'W' },
  black: { man: 'b', king: 'B' },
};

function buildInitialBoard(ruleSet: RuleSet): BoardCell[] {
  const { boardSize, startingRows } = ruleSet;
  const cells = new Array<BoardCell>(boardSize * boardSize).fill(null);
  for (let row = 0; row < boardSize; row += 1) {
    for (let col = 0; col < boardSize; col += 1) {
      const square: Square = { row, col };
      if (ruleSet.piecesOnDarkSquares && !isDarkSquare(square)) continue;
      if (row < startingRows) {
        cells[indexOf(square, boardSize)] = { color: 'black', kind: 'man' };
      } else if (row >= boardSize - startingRows) {
        cells[indexOf(square, boardSize)] = { color: 'white', kind: 'man' };
      }
    }
  }
  return cells;
}

export class GameState {
  static initial(ruleSet: RuleSet): GameState {
    return new GameState(
      ruleSet,
      buildInitialBoard(ruleSet),
      ruleSet.firstPlayer,
      0,
      0,
      [],
    );
  }

  /**
   * Build a state from a sparse piece map. Used by tests, debug presets, and
   * replay restoration. Counters default to a fresh game; pass `init` to
   * override the move count, quiet-move counter, or position history.
   */
  static custom(
    ruleSet: RuleSet,
    pieces: Iterable<{ square: Square; piece: Piece }>,
    currentPlayer: Color,
    init: { moveCount?: number; quietMoveCount?: number; positionHistory?: ReadonlyArray<string> } = {},
  ): GameState {
    const cells = new Array<BoardCell>(ruleSet.boardSize * ruleSet.boardSize).fill(null);
    for (const { square, piece } of pieces) {
      cells[indexOf(square, ruleSet.boardSize)] = piece;
    }
    return new GameState(
      ruleSet,
      cells,
      currentPlayer,
      init.moveCount ?? 0,
      init.quietMoveCount ?? 0,
      init.positionHistory ?? [],
    );
  }

  private constructor(
    readonly ruleSet: RuleSet,
    private readonly cells: ReadonlyArray<BoardCell>,
    readonly currentPlayer: Color,
    readonly moveCount: number,
    readonly quietMoveCount: number,
    readonly positionHistory: ReadonlyArray<string>,
  ) {}

  get boardSize(): number {
    return this.ruleSet.boardSize;
  }

  pieceAt(square: Square): BoardCell {
    if (
      square.row < 0 ||
      square.row >= this.boardSize ||
      square.col < 0 ||
      square.col >= this.boardSize
    ) {
      return null;
    }
    return this.cells[indexOf(square, this.boardSize)] ?? null;
  }

  /** Iterate every occupied square. */
  *pieces(): Generator<{ square: Square; piece: Piece }> {
    for (let row = 0; row < this.boardSize; row += 1) {
      for (let col = 0; col < this.boardSize; col += 1) {
        const piece = this.cells[row * this.boardSize + col];
        if (piece) yield { square: { row, col }, piece };
      }
    }
  }

  /** Returns the FEN-like hash of the current position + side to move. */
  hash(): string {
    return GameState.hash(this.cells, this.boardSize, this.currentPlayer);
  }

  applyMove(move: Move): GameState {
    const mover = this.pieceAt(move.from);
    if (!mover) {
      throw new Error(`No piece at ${move.from.row},${move.from.col}`);
    }
    if (mover.color !== this.currentPlayer) {
      throw new Error(`Wrong color to move: ${mover.color}`);
    }
    const next = this.cells.slice();
    next[indexOf(move.from, this.boardSize)] = null;
    for (const captured of move.captures) {
      next[indexOf(captured, this.boardSize)] = null;
    }
    const promoted = move.promoted || isPromotionSquare(move.to, mover.color, this.boardSize);
    next[indexOf(move.to, this.boardSize)] = {
      color: mover.color,
      kind: promoted ? 'king' : mover.kind,
    };

    const nextPlayer = opponentOf(this.currentPlayer);
    const quiet = isQuietMove(move, mover);
    const nextQuiet = quiet ? this.quietMoveCount + 1 : 0;
    const nextHistory = quiet
      ? [...this.positionHistory, GameState.hash(next, this.boardSize, nextPlayer)]
      : []; // Any capture or man advance resets the repetition window.
    return new GameState(
      this.ruleSet,
      next,
      nextPlayer,
      this.moveCount + 1,
      nextQuiet,
      nextHistory,
    );
  }

  private static hash(
    cells: ReadonlyArray<BoardCell>,
    boardSize: number,
    player: Color,
  ): string {
    let out = '';
    for (let i = 0; i < boardSize * boardSize; i += 1) {
      const piece = cells[i];
      out += piece ? PIECE_GLYPH[piece.color][piece.kind] : '.';
    }
    return `${out}:${player[0]}`;
  }
}

function isQuietMove(move: Move, mover: Piece): boolean {
  if (move.captures.length > 0) return false;
  if (mover.kind === 'man') return false; // man advance always resets
  if (move.promoted) return false; // promotion isn't quiet
  return true;
}
